import React, { useState, useEffect, useContext } from 'react';
import { Container, Card, Row, Col, Button, Badge, ListGroup, Spinner, Alert, Modal, Table } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { getFeatureDefinitions } from '../services/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import { getPlanFeaturesList, formatPrice } from '../utils/planUtils';
import { FaCreditCard, FaTrash, FaStar } from 'react-icons/fa';
import SubscribePlanGuide from '../components/SubscribePlanGuide';
import { isSeen } from '../utils/onboardingSeen';

const PlanManagementPage = () => {
    const { t, i18n } = useTranslation();
    const { user, business, refreshBusiness } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [currentPlan, setCurrentPlan] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [featureDefinitions, setFeatureDefinitions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' (Subscription) or 'pse' (One-Time)

    // Billing: cards & payment history
    const [cards, setCards] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(true);
    const [paymentsLoading, setPaymentsLoading] = useState(true);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [cardToRemove, setCardToRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [settingDefault, setSettingDefault] = useState(null);

    useEffect(() => {
        fetchPlans();
        fetchCards();
        fetchPaymentHistory();
    }, []);

    // Al llegar desde el banner ("Suscripción"), revela los planes con un scroll ligero —
    // mismo criterio que BusinessSettingsPage.js: solo si SubscribePlanGuide no va a correr
    // (si va a correr, es ella quien guía el scroll paso a paso, empezando más arriba, en el
    // selector de método de pago).
    useEffect(() => {
        if (loading || !location.state?.scrollToPlans) return;
        const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);
        const guideWillRun = user?.role === 'owner' && !isSubscriptionActive
            && !isSeen(user.id, 'subscribe_plans_guide');
        if (guideWillRun) return;
        document.getElementById('onboarding-plans-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const fetchPlans = async () => {
        try {
            const [currentResponse, plansResponse, featuresResponse] = await Promise.all([
                api.get('/plans/current'),
                api.get('/plans'),
                getFeatureDefinitions()
            ]);

            setCurrentPlan(currentResponse.data);
            setAvailablePlans(plansResponse.data);
            setFeatureDefinitions(featuresResponse.data);
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error(t('planManagement.errorLoadingPlans'));
        } finally {
            setLoading(false);
        }
    };

    const handlePlanChange = async (planId) => {
        setUpdating(true);
        try {
            // Call payment creation endpoint based on selected method
            let endpoint = '/payments/subscriptions/create';
            if (paymentMethod === 'pse') {
                endpoint = '/payments/preferences/create';
            }

            const response = await api.post(endpoint, { plan_id: planId });

            if (response.data.init_point) {
                // Redirect to Mercado Pago
                window.location.href = response.data.init_point;
            } else {
                toast.error(t('planManagement.errorCreatingSubscription'));
            }
        } catch (error) {
            if (error.response?.data?.warnings) {
                setWarnings(error.response.data.warnings);
                setShowWarningModal(true);
            } else {
                toast.error(error.response?.data?.message || t('planManagement.errorUpdatingPlan'));
            }
        } finally {
            setUpdating(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm(t('planManagement.confirmCancel'))) return;

        setUpdating(true);
        try {
            await api.put('/payments/subscriptions/cancel', { subscription_id: currentPlan.subscription_id });
            toast.success(t('planManagement.subscriptionCancelled'));
            await fetchPlans();
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            toast.error(t('planManagement.errorCancelling'));
        } finally {
            setUpdating(false);
        }
    };



    const handleSelectPlan = (plan) => {
        setSelectedPlan(plan);
        handlePlanChange(plan.id);
    };

    const getPlanBadgeVariant = (planName) => {
        switch (planName) {
            case 'entrepreneur':
                return 'primary';
            case 'growth':
                return 'success';
            case 'business_network':
                return 'warning';
            default:
                return 'secondary';
        }
    };

    const translatePlanName = (planKey) => {
        const key = planKey === 'business_network' ? 'business_network' : planKey;
        return t(`planManagement.planNames.${key}`, planKey);
    };

    const fetchCards = async () => {
        try {
            const res = await api.get('/billing/cards');
            setCards(res.data);
        } catch (error) {
            console.error('Error fetching cards:', error);
        } finally {
            setCardsLoading(false);
        }
    };

    const fetchPaymentHistory = async () => {
        try {
            const res = await api.get('/billing/payments');
            setPaymentHistory(res.data);
        } catch (error) {
            console.error('Error fetching payment history:', error);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const handleRemoveCard = async () => {
        if (!cardToRemove) return;
        setRemoving(true);
        try {
            await api.delete(`/billing/cards/${cardToRemove.id}`);
            setCards(cards.filter(c => c.id !== cardToRemove.id));
            toast.success(t('billing.cardRemoved'));
        } catch (error) {
            if (error.response?.status === 422) {
                toast.error(t('billing.cannotRemoveLastCard'));
            } else {
                toast.error(t('billing.errorRemovingCard'));
            }
        } finally {
            setRemoving(false);
            setShowRemoveModal(false);
            setCardToRemove(null);
        }
    };

    const handleSetDefault = async (card) => {
        setSettingDefault(card.id);
        try {
            await api.put(`/billing/cards/${card.id}/default`);
            setCards(cards.map(c => ({ ...c, is_default: c.id === card.id })));
            toast.success(t('billing.defaultCardUpdated'));
        } catch (error) {
            toast.error(t('billing.errorSettingDefault'));
        } finally {
            setSettingDefault(null);
        }
    };

    const getPaymentStatusBadge = (status) => {
        const variants = { approved: 'success', pending: 'warning', rejected: 'danger', cancelled: 'secondary' };
        return <Badge bg={variants[status] || 'secondary'}>{t(`billing.paymentStatus.${status}`, status)}</Badge>;
    };

    const getCardBrand = (card) => {
        const method = card.payment_method?.id || card.payment_method?.name || '';
        return method.charAt(0).toUpperCase() + method.slice(1);
    };



    if (loading) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" />
            </Container>
        );
    }

    if (!currentPlan) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{t('planManagement.couldNotLoadPlan')}</Alert>
            </Container>
        );
    }

    // Only the real business owner can change plans: either the effective role is 'owner',
    // or the active business's owner_id matches the user (covers a superadmin who owns it).
    const canChangePlan =
        user?.role === 'owner' ||
        (business?.owner_id != null && String(business.owner_id) === String(user?.id));

    return (
        <Container className="mt-4">
            <h2 className="mb-4">{t('planManagement.title')}</h2>

            {currentPlan.subscription_status === 'pending' && (
                <Alert variant="warning" className="mb-4">
                    <Alert.Heading>{t('planManagement.subscriptionPendingTitle')}</Alert.Heading>
                    <p>
                        {t('planManagement.subscriptionPendingMessage')}
                    </p>
                </Alert>
            )}

            {/* Current Plan Card */}
            <Card className="mb-4 shadow-sm">
                <Card.Header as="h4" className="bg-primary text-white">
                    {t('planManagement.currentPlan')}
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <h3>
                                {translatePlanName(currentPlan.plan_name)}
                                <Badge bg={getPlanBadgeVariant(currentPlan.plan_name)} className="ms-2">
                                    {currentPlan.subscription_status}
                                </Badge>
                            </h3>
                            <p className="text-muted display-6">
                                <strong>{formatPrice(currentPlan.price, currentPlan.currency)}</strong> <span className="fs-6">/mes</span>
                            </p>
                            <p className="small text-muted">
                                {t('planManagement.startDate')}: {new Date(currentPlan.subscription_start_date).toLocaleDateString()}
                            </p>
                            {currentPlan.is_one_time && currentPlan.subscription_end_date && (
                                <Alert
                                    variant={new Date(currentPlan.subscription_end_date) < new Date() ? 'danger' : 'warning'}
                                    className="py-2 mb-2"
                                >
                                    <small>
                                        {new Date(currentPlan.subscription_end_date) < new Date()
                                            ? t('planManagement.oneTimeExpired')
                                            : t('planManagement.oneTimeExpiresAt', { date: new Date(currentPlan.subscription_end_date).toLocaleDateString() })}
                                    </small>
                                </Alert>
                            )}
                            {currentPlan.subscription_id && !currentPlan.is_one_time && (currentPlan.subscription_status === 'active' || currentPlan.subscription_status === 'trialing') && (
                                <div className="mt-3">
                                    {currentPlan.subscription_status === 'trialing' && (
                                        <Alert variant="info" className="py-2 mb-2">
                                            <small>
                                                {t('planManagement.trialEndsAt', { date: new Date(currentPlan.trial_ends_at).toLocaleDateString() })}
                                            </small>
                                        </Alert>
                                    )}
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleCancelSubscription}
                                        disabled={updating}
                                    >
                                        {t('planManagement.cancelSubscription')}
                                    </Button>
                                </div>
                            )}
                        </Col>
                        <Col md={6}>
                            <h5>{t('planManagement.usage')}</h5>
                            <Row className="gy-2">
                                <Col xs={6}>
                                    <div className="p-2 border rounded bg-light h-100">
                                        <strong>{t('planManagement.users')}:</strong><br />
                                        {currentPlan.usage.users} / {currentPlan.max_users === -1 ? t('planManagement.unlimited') : currentPlan.max_users}
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="p-2 border rounded bg-light h-100">
                                        <strong>{t('planManagement.businesses')}:</strong><br />
                                        {currentPlan.usage.businesses} / {currentPlan.max_businesses}
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="p-2 border rounded bg-light h-100">
                                        <strong>{t('planManagement.products')}:</strong><br />
                                        {currentPlan.usage.products} / {currentPlan.max_products === -1 ? t('planManagement.unlimited') : currentPlan.max_products}
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="p-2 border rounded bg-light h-100">
                                        {currentPlan.features &&
                                            currentPlan.features.max_sales_with_installments !== -1 &&
                                            currentPlan.features.max_sales_with_installments !== currentPlan.monthly_sales_limit ? (
                                            <div className="d-flex align-items-center h-100">
                                                <div className="flex-fill text-center border-end border-secondary pe-1">
                                                    <strong>{t('planManagement.featureNames.sales')}:</strong><br />
                                                    {currentPlan.usage.sales} / {(currentPlan.monthly_sales_limit === null || currentPlan.monthly_sales_limit >= 50000) ? t('planManagement.unlimited') : currentPlan.monthly_sales_limit}
                                                </div>
                                                <div className="flex-fill text-center ps-1">
                                                    <strong>{t('planManagement.featureNames.sales_with_installments')}:</strong><br />
                                                    {currentPlan.usage.sales_with_installments !== undefined ? (
                                                        <>
                                                            {currentPlan.usage.sales_with_installments} / {currentPlan.features.max_sales_with_installments}
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <strong>{t('planManagement.featureNames.sales')}:</strong><br />
                                                {currentPlan.usage.sales} / {(currentPlan.monthly_sales_limit === null || currentPlan.monthly_sales_limit >= 50000) ? t('planManagement.unlimited') : currentPlan.monthly_sales_limit}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Warning Modal */}
            <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle-fill text-warning me-2" style={{ fontSize: '24px' }}></i>
                        {t('planManagement.cannotChangePlan')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        <p className="mb-3">
                            {t('planManagement.resolveRestrictions')}
                        </p>
                        {warnings.map((warning, index) => (
                            <Alert key={index} variant="danger" className="mb-2">
                                <strong>{warning.type === 'users' ? t('planManagement.users') : t('planManagement.products')}:</strong> {warning.message}
                            </Alert>
                        ))}
                    </Alert>
                    <p className="text-muted small">
                        {t('planManagement.adjustAndRetry')}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowWarningModal(false)}>
                        {t('planManagement.close')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Métodos de Pago */}
            <h3 className="mt-5 mb-3">{t('billing.paymentMethods')}</h3>
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    {cardsLoading ? (
                        <div className="text-center"><Spinner animation="border" size="sm" /></div>
                    ) : cards.length === 0 ? (
                        <Alert variant="info" className="mb-0">{t('billing.noCards')}</Alert>
                    ) : (
                        cards.map(card => (
                            <div key={card.id} className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
                                <div className="d-flex align-items-center">
                                    <FaCreditCard className="me-3 text-primary" size={24} />
                                    <div>
                                        <strong>{getCardBrand(card)}</strong> •••• {card.last_four_digits}
                                        <br />
                                        <small className="text-muted">
                                            {t('billing.expires')} {card.expiration_month}/{card.expiration_year}
                                        </small>
                                    </div>
                                    {card.is_default && (
                                        <Badge bg="primary" className="ms-2">{t('billing.defaultBadge')}</Badge>
                                    )}
                                </div>
                                <div>
                                    {!card.is_default && (
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleSetDefault(card)}
                                            disabled={settingDefault === card.id}
                                        >
                                            {settingDefault === card.id
                                                ? <Spinner animation="border" size="sm" />
                                                : <><FaStar className="me-1" />{t('billing.setDefault')}</>}
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => { setCardToRemove(card); setShowRemoveModal(true); }}
                                        disabled={cards.length <= 1}
                                        title={cards.length <= 1 ? t('billing.cannotRemoveLastCard') : ''}
                                    >
                                        <FaTrash />
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </Card.Body>
            </Card>

            {/* Available Plans */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">{t('planManagement.availablePlans')}</h3>
                <Button variant="outline-info" onClick={() => navigate('/plans-comparison')}>
                    {t('planManagement.viewDetailedComparison')}
                </Button>
            </div>
            {!canChangePlan && (
                <Alert variant="warning">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {t('planManagement.onlyOwnerCanChange')}
                </Alert>
            )}

            {/* Payment Method Toggle */}
            <div id="onboarding-payment-method-toggle" className="d-flex justify-content-center mb-4">
                <div className="bg-light p-1 rounded-pill d-inline-flex border">
                    <Button
                        variant={paymentMethod === 'card' ? 'primary' : 'light'}
                        className="rounded-pill px-4"
                        onClick={() => setPaymentMethod('card')}
                    >
                        <i className="bi bi-credit-card-fill me-2"></i>
                        {t('planManagement.subscriptionCard')}
                    </Button>
                    <Button
                        variant={paymentMethod === 'pse' ? 'primary' : 'light'}
                        className="rounded-pill px-4"
                        onClick={() => setPaymentMethod('pse')}
                    >
                        <span className="me-2 fw-bold">{t('planManagement.pseNequi')}</span>
                        {t('planManagement.manualPayment')}
                    </Button>
                </div>
            </div>

            {paymentMethod === 'card' ? (
                !['active', 'trialing'].includes(currentPlan.subscription_status) && (
                    <Alert variant="info" className="text-center mb-4">
                        <i className="bi bi-star-fill me-2 text-warning"></i>
                        <strong>{t('planManagement.oneMonthFree')}</strong>
                    </Alert>
                )
            ) : (
                <Alert variant="secondary" className="text-center mb-4">
                    <i className="bi bi-info-circle-fill me-2"></i>
                    {t('planManagement.trialNoteManual')}
                </Alert>
            )}

            <SubscribePlanGuide />
            <Row id="onboarding-plans-section">
                {availablePlans.map((plan) => {
                    const isCurrentPlan = plan.id === currentPlan.plan_id;
                    const planPrice = parseFloat(plan.price);
                    const currentPrice = parseFloat(currentPlan.price);
                    const isUpgrade = planPrice > currentPrice;

                    return (
                        <Col md={4} key={plan.id} className="mb-4">
                            <Card className={`h-100 ${isCurrentPlan ? 'border-primary shadow' : 'shadow-sm'}`}>
                                <Card.Header className={isCurrentPlan ? 'bg-primary text-white text-center' : 'text-center bg-white border-bottom-0 pt-4'}>
                                    <h4 className={isCurrentPlan ? '' : 'text-primary'}>
                                        {translatePlanName(plan.name)}
                                    </h4>
                                    {isCurrentPlan && <Badge bg="light" text="dark" className="mt-1">{t('planManagement.currentPlanBadge')}</Badge>}
                                </Card.Header>
                                <Card.Body className="d-flex flex-column">
                                    <h3 className="text-center mb-3">
                                        {formatPrice(plan.price, plan.currency)} <small className="text-muted fs-6">{t('planManagement.perMonth')}</small>
                                    </h3>

                                    <ul className="list-unstyled mt-3 mb-4 text-start mx-auto">
                                        {getPlanFeaturesList(plan, t, featureDefinitions, i18n.language).map((feature, index) => (
                                            <li key={`feat-${index}`} className="mb-2">
                                                ✓ {feature}
                                            </li>
                                        ))}
                                    </ul>

                                    {(!isCurrentPlan || currentPlan.subscription_status === 'pending' || paymentMethod === 'pse') && canChangePlan && (
                                        <>
                                            <Button
                                                variant={isCurrentPlan && paymentMethod !== 'pse' ? 'primary' : (isUpgrade ? 'primary' : 'outline-primary')}
                                                className="w-100 mt-auto rounded-pill"
                                                onClick={() => handleSelectPlan(plan)}
                                                disabled={updating || !user.email_verified_at}
                                            >
                                                {updating ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                                        {t('planManagement.updating')}
                                                    </>
                                                ) : (
                                                    paymentMethod === 'pse'
                                                        ? t('planManagement.selectPlan')
                                                        : (['active', 'trialing'].includes(currentPlan.subscription_status)
                                                            ? (isUpgrade ? t('planManagement.upgradeButton', 'Mejorar Plan') : t('planManagement.changePlanButton', 'Cambiar Plan'))
                                                            : t('landingPage.pricing.entrepreneur.cta', 'Iniciar Prueba Gratis'))
                                                )}
                                            </Button>
                                            {/* Tarea 027c — el backend (requireVerifiedEmail) es la fuente de verdad; esto es solo UX. */}
                                            {!user.email_verified_at && (
                                                <p className="text-center text-muted small mt-2 mb-0">
                                                    {t('planManagement.verifyEmailToSubscribe')}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            {/* Historial de Pagos */}
            <h3 className="mb-3">{t('billing.paymentHistory')}</h3>
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    {paymentsLoading ? (
                        <div className="text-center"><Spinner animation="border" size="sm" /></div>
                    ) : paymentHistory.length === 0 ? (
                        <Alert variant="info" className="mb-0">{t('billing.noPayments')}</Alert>
                    ) : (
                        <Table responsive hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>{t('billing.date')}</th>
                                    <th>{t('billing.amount')}</th>
                                    <th>{t('billing.plan')}</th>
                                    <th>{t('billing.status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paymentHistory.map(payment => (
                                    <tr key={payment.id}>
                                        <td>{new Date(payment.created_at).toLocaleDateString('es-CO')}</td>
                                        <td>{formatPrice(payment.amount, payment.currency)}</td>
                                        <td>{translatePlanName(payment.plan_name)}</td>
                                        <td>{getPaymentStatusBadge(payment.status)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Remove Card Modal */}
            <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t('billing.removeCardTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {cardToRemove && (
                        <p>{t('billing.removeCardConfirm', { brand: getCardBrand(cardToRemove), last4: cardToRemove.last_four_digits })}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>{t('cancel')}</Button>
                    <Button variant="danger" onClick={handleRemoveCard} disabled={removing}>
                        {removing ? <Spinner animation="border" size="sm" /> : t('billing.removeCard')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default PlanManagementPage;